/**
 * 皮肤管理上下文 SkinContext
 * 负责在全局管理和响应前端功能主页面的皮肤风格（如中秋孟菲斯风格、默认经典科技风）
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { getActiveSkin, getCachedActiveSkin, DEFAULT_FALLBACK_SKIN } from "../api/skinApi";
import Logger from "../utils/logger";

const SkinContext = createContext(null);

export const SkinProvider = ({ children }) => {
  // 默认采用极简科技风，减轻前端功能主页面渲染压力
  const [skin, setSkin] = useState(() => DEFAULT_FALLBACK_SKIN);

  // 同步属性到 DOM
  const applySkinToDOM = useCallback((skinObj) => {
    const skinKey = skinObj?.skin_key || "default";

    // 1. 设置 html 标签上的 data-skin 属性
    document.documentElement.setAttribute("data-skin", skinKey);

    // 2. 在 body 上设置辅助 class
    document.body.classList.remove("skin-default", "skin-mid_autumn_memphis", "skin-national_day_classical");
    document.body.classList.add(`skin-${skinKey}`);

    // 3. 设置主题色 CSS 变量（如果配置了特殊主题色）
    if (skinObj?.theme_color) {
      document.documentElement.style.setProperty("--skin-theme-color", skinObj.theme_color);
    }
  }, []);

  // 组件挂载时应用默认极简皮肤并清理旧的节日皮肤本地缓存
  useEffect(() => {
    applySkinToDOM(DEFAULT_FALLBACK_SKIN);
    try {
      localStorage.removeItem("hammerath_active_skin");
    } catch (e) {}
  }, [applySkinToDOM]);

  // 手动刷新皮肤配置
  const refreshSkin = useCallback(async () => {
    return DEFAULT_FALLBACK_SKIN;
  }, []);

  // 切换皮肤（保留接口兼容）
  const switchPreviewSkin = useCallback((targetSkinKey) => {
    const target = DEFAULT_FALLBACK_SKIN;
    setSkin(target);
    applySkinToDOM(target);
  }, [applySkinToDOM]);

  const value = {
    skin,
    skinKey: "default",
    skinConfig: DEFAULT_FALLBACK_SKIN.config,
    isMidAutumnMemphis: false,
    isNationalDayClassical: false,
    isDefaultSkin: true,
    refreshSkin,
    switchPreviewSkin,
  };

  return <SkinContext.Provider value={value}>{children}</SkinContext.Provider>;
};

export const useSkin = () => {
  const context = useContext(SkinContext);
  if (!context) {
    // 容错降级：在未包裹 Provider 的孤立场景下提供默认值
    return {
      skin: DEFAULT_FALLBACK_SKIN,
      skinKey: "default",
      skinConfig: DEFAULT_FALLBACK_SKIN.config,
      isMidAutumnMemphis: false,
      isNationalDayClassical: false,
      isDefaultSkin: true,
      refreshSkin: () => Promise.resolve(DEFAULT_FALLBACK_SKIN),
      switchPreviewSkin: () => {},
    };
  }
  return context;
};

export default SkinContext;

